import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Icon from '../components/Icon';
import {
  fetchWaitlist,
  fetchWaitlistSummary,
  patchWaitlistSignup,
} from '../services/api';
import { timeAgo } from '../utils/format';

// Outreach status taxonomy. Mirrors the CHECK constraint in
// supabase/migrations/0014_waitlist_dashboard.sql.
const STATUS_BADGE = {
  new:            { class: 'badge-info',    label: 'New' },
  contacted:      { class: 'badge-warning', label: 'Contacted' },
  access_granted: { class: 'badge-success', label: 'Access granted' },
  declined:       { class: 'badge-danger',  label: 'Declined' },
};

const TYPE_LABEL = (row) => {
  if (row.newsletter_opt_in && row.status !== 'new') return 'Both';
  if (row.newsletter_opt_in) return 'Newsletter';
  return 'Waitlist';
};

const MST_TZ = 'America/Edmonton';
const fmtMST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: MST_TZ,
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
};

const Waitlist = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all | new | contacted | access_granted | declined
  const [query, setQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [newsletterFilter, setNewsletterFilter] = useState(''); // '' | 'true' | 'false'
  const [selected, setSelected] = useState(null);
  const [draftNotes, setDraftNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      if (countryFilter) params.country = countryFilter;
      if (newsletterFilter) params.newsletter = newsletterFilter;
      if (query.trim()) params.q = query.trim();

      const [list, sum] = await Promise.all([
        fetchWaitlist(params),
        fetchWaitlistSummary().catch(() => null),
      ]);
      setRows(list?.data || []);
      if (sum) setSummary(sum);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [activeTab, countryFilter, newsletterFilter, query]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = useCallback(async (id, status) => {
    setBusy(true);
    try {
      const updated = await patchWaitlistSignup(id, { status });
      // Optimistically replace the row + refresh summary counts.
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
      if (selected?.id === id) setSelected(updated);
      const sum = await fetchWaitlistSummary().catch(() => null);
      if (sum) setSummary(sum);
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  }, [selected]);

  const saveNotes = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const updated = await patchWaitlistSignup(selected.id, { notes: draftNotes });
      setRows((prev) => prev.map((r) => (r.id === selected.id ? { ...r, ...updated } : r)));
      setSelected(updated);
    } catch (err) {
      setError(err.message || 'Note save failed');
    } finally {
      setBusy(false);
    }
  }, [selected, draftNotes]);

  // Country options derived from loaded rows so we don't need a separate
  // call. Sorted by frequency descending (most-common countries first).
  const countryOptions = useMemo(() => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.country_sending_from) counts.set(r.country_sending_from, (counts.get(r.country_sending_from) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([code]) => code);
  }, [rows]);

  const tabCount = useCallback((tab) => {
    if (!summary) return null;
    if (tab === 'all') return summary.total;
    return summary.by_status?.[tab] ?? null;
  }, [summary]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Waitlist · Newsletter</h1>
          <p className="page-sub">
            Pre-launch signups from coynitt.ca. Track who's been contacted and who's
            been granted early access. Newsletter-only subscribers stay in the
            <em> New</em> state — promote them as you onboard.
          </p>
        </div>
      </header>

      {/* Stats strip */}
      {summary && (
        <div className="stat-grid">
          <Stat label="Total signups"   value={summary.total} />
          <Stat label="New"             value={summary.by_status?.new ?? 0}            tone="info" />
          <Stat label="Contacted"       value={summary.by_status?.contacted ?? 0}      tone="warn" />
          <Stat label="Access granted"  value={summary.by_status?.access_granted ?? 0} tone="ok" />
          <Stat label="Newsletter opt-in" value={summary.newsletter_opt_in}            tone="info" />
        </div>
      )}

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="tabs">
          {[
            ['all',            'All'],
            ['new',            'New'],
            ['contacted',      'Contacted'],
            ['access_granted', 'Access granted'],
            ['declined',       'Declined'],
          ].map(([key, label]) => {
            const n = tabCount(key);
            return (
              <button
                key={key}
                className={clsx('tab', activeTab === key && 'tab-active')}
                onClick={() => setActiveTab(key)}
              >
                {label}{n != null && <span className="tab-count">{n}</span>}
              </button>
            );
          })}
        </div>

        <div className="row row-gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="Search email or name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          />
          <select className="input" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} style={{ minWidth: 120 }}>
            <option value="">All countries</option>
            {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="input" value={newsletterFilter} onChange={(e) => setNewsletterFilter(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">Waitlist + newsletter</option>
            <option value="true">Newsletter opt-in only</option>
            <option value="false">No newsletter</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>
      )}

      {/* Table */}
      <div className="card table-wrap" style={{ marginTop: 16 }}>
        {loading && rows.length === 0 ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No signups match this filter.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Country</th>
                <th>Corridors</th>
                <th>Type</th>
                <th>Source</th>
                <th>Joined</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => { setSelected(r); setDraftNotes(r.notes || ''); }} style={{ cursor: 'pointer' }}>
                  <td><span className="mono" style={{ wordBreak: 'break-all' }}>{r.email}</span></td>
                  <td>{r.first_name || '—'}</td>
                  <td>{r.country_sending_from || '—'}</td>
                  <td>
                    {(r.corridors_of_interest || []).length > 0
                      ? (r.corridors_of_interest || []).join(', ')
                      : <span style={{ color: 'var(--tx3)' }}>—</span>}
                  </td>
                  <td>{TYPE_LABEL(r)}</td>
                  <td><span style={{ color: 'var(--tx3)', fontSize: 12 }}>{r.source || '—'}</span></td>
                  <td title={fmtMST(r.created_at)} style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.created_at)}</td>
                  <td>
                    <span className={clsx('badge', STATUS_BADGE[r.status]?.class)}>
                      {STATUS_BADGE[r.status]?.label || r.status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row row-gap-1">
                      {r.status !== 'contacted' && (
                        <button className="btn btn-xs" disabled={busy} onClick={() => updateStatus(r.id, 'contacted')}>
                          Mark contacted
                        </button>
                      )}
                      {r.status !== 'access_granted' && (
                        <button className="btn btn-xs btn-primary" disabled={busy} onClick={() => updateStatus(r.id, 'access_granted')}>
                          Grant access
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Side drawer */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" style={{ maxWidth: 520, width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Signup detail</h3>
              <button className="icon-btn" onClick={() => setSelected(null)} aria-label="Close">
                <Icon name="x" size={18} />
              </button>
            </div>

            <div className="modal-body">
              <DetailRow label="Email"      value={<span className="mono" style={{ wordBreak: 'break-all' }}>{selected.email}</span>} />
              <DetailRow label="Name"       value={selected.first_name || '—'} />
              <DetailRow label="Country"    value={selected.country_sending_from || '—'} />
              <DetailRow label="Corridors"  value={(selected.corridors_of_interest || []).join(', ') || '—'} />
              <DetailRow label="Newsletter" value={selected.newsletter_opt_in ? 'Yes' : 'No'} />
              <DetailRow label="Source"     value={selected.source || '—'} />
              {selected.utm_source && (
                <DetailRow label="UTM" value={`${selected.utm_source || ''} / ${selected.utm_medium || ''} / ${selected.utm_campaign || ''}`} />
              )}
              <DetailRow label="Joined"     value={fmtMST(selected.created_at)} />

              <hr className="div-soft" />

              <DetailRow label="Status"
                value={<span className={clsx('badge', STATUS_BADGE[selected.status]?.class)}>{STATUS_BADGE[selected.status]?.label}</span>} />
              {selected.contacted_at && (
                <DetailRow label="Contacted"
                  value={`${fmtMST(selected.contacted_at)}${selected.contacted_profile?.full_name ? ` · by ${selected.contacted_profile.full_name}` : ''}`} />
              )}
              {selected.access_granted_at && (
                <DetailRow label="Access granted"
                  value={`${fmtMST(selected.access_granted_at)}${selected.access_granted_profile?.full_name ? ` · by ${selected.access_granted_profile.full_name}` : ''}`} />
              )}
              {selected.declined_at && (
                <DetailRow label="Declined"
                  value={`${fmtMST(selected.declined_at)}${selected.declined_profile?.full_name ? ` · by ${selected.declined_profile.full_name}` : ''}`} />
              )}

              <hr className="div-soft" />

              <label className="fld-label" htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="input"
                rows={4}
                placeholder="Internal notes (e.g. intro source, conversation log)…"
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />

              <div className="row row-gap-2" style={{ marginTop: 16, flexWrap: 'wrap' }}>
                <button className="btn" disabled={busy || draftNotes === (selected.notes || '')} onClick={saveNotes}>Save notes</button>
                <button className="btn" disabled={busy || selected.status === 'contacted'}      onClick={() => updateStatus(selected.id, 'contacted')}>Mark contacted</button>
                <button className="btn btn-primary" disabled={busy || selected.status === 'access_granted'} onClick={() => updateStatus(selected.id, 'access_granted')}>Grant access</button>
                <button className="btn btn-danger"  disabled={busy || selected.status === 'declined'}       onClick={() => updateStatus(selected.id, 'declined')}>Decline</button>
                {selected.status !== 'new' && (
                  <button className="btn btn-ghost" disabled={busy} onClick={() => updateStatus(selected.id, 'new')}>Reset to new</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <div className={clsx('stat-card', tone && `stat-${tone}`)}>
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value}</div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="row row-gap-3" style={{ padding: '6px 0', alignItems: 'flex-start', flexWrap: 'wrap' }}>
    <div style={{ minWidth: 110, color: 'var(--tx3)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ flex: 1, minWidth: 200 }}>{value}</div>
  </div>
);

export default Waitlist;
